import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@sizilianer/core/apiFetch';
import { currentGuestLanguage } from '@/i18n';
import type {
  ApeCalculateResponse,
  AvailabilityResponse,
  ApeRequestFormData,
  BookingLookup,
  PublicEvent,
  QuoteLookup,
} from './types';

/**
 * Lädt Verfügbarkeits-Kalender (3 Monate per default).
 */
export function useApeAvailability(monthsAhead = 3) {
  return useQuery({
    queryKey: ['ape', 'availability', monthsAhead],
    queryFn: async (): Promise<AvailabilityResponse> => {
      const r = await apiFetch(`/api/ape-availability?months=${monthsAhead}`);
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Verfügbarkeit konnte nicht geladen werden');
      }
      return (await r.json()) as AvailabilityResponse;
    },
    staleTime: 60_000,
  });
}

/**
 * Lädt Liste der bestätigten öffentlichen Events (zukünftig).
 */
export function useApePublicEvents() {
  return useQuery({
    queryKey: ['ape', 'public-events'],
    queryFn: async (): Promise<PublicEvent[]> => {
      const r = await apiFetch('/api/ape-public-events');
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Öffentliche Events konnten nicht geladen werden');
      }
      const body = (await r.json()) as { events: PublicEvent[] };
      return body.events;
    },
    staleTime: 60_000,
  });
}

/**
 * Einzelnes öffentliches Event für /events/:slug (0098).
 * 404 wird als `null` gemeldet — die Seite rendert dann <NotFound />
 * statt einen Fehlerzustand; echte Fehler (Netz, 5xx) werfen weiter.
 */
export function useApePublicEvent(slug: string | null) {
  return useQuery({
    queryKey: ['ape', 'public-event', slug],
    queryFn: async (): Promise<PublicEvent | null> => {
      const r = await apiFetch(
        `/api/ape-public-event?slug=${encodeURIComponent(slug ?? '')}`,
      );
      if (r.status === 404) return null;
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Event konnte nicht geladen werden');
      }
      const body = (await r.json()) as { event: PublicEvent };
      return body.event;
    },
    enabled: Boolean(slug),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Unverbindliche Sofort-Schätzung (0098) — bewusst eine MUTATION, kein
 * Query: gerechnet wird NUR auf Klick (Nominatim-Policy verbietet
 * Autocomplete), und ein Query-Cache würde Staleness nur verschleiern.
 */
export function useApeCalculate() {
  return useMutation({
    mutationFn: async (input: {
      postal_code: string;
      city: string;
      persons: number;
    }): Promise<ApeCalculateResponse> => {
      const r = await apiFetch('/api/ape-quote-calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Schätzung derzeit nicht möglich');
      }
      return (await r.json()) as ApeCalculateResponse;
    },
  });
}

/**
 * Submit der Anfrage (Flow A).
 */
export function useApeRequest() {
  return useMutation({
    mutationFn: async (
      data: ApeRequestFormData,
    ): Promise<{ ok: true; id: string }> => {
      const r = await apiFetch('/api/ape-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: data.date,
          start_time: data.start_time,
          end_time: data.end_time ?? null,
          expected_guests: data.expected_guests,
          event_type: data.event_type,
          is_public: data.is_public,
          // ⚠️ ZWEITES Feld-Mapping. Zod entfernt unbekannte Schlüssel
          // stillschweigend — ein hier vergessenes Feld geht LAUTLOS
          // verloren, ohne Typfehler und ohne roten Test.
          location_name: data.location_name ?? null,
          location_address: data.location_address ?? null,
          location_street: data.location_street ?? null,
          location_zip: data.location_zip ?? null,
          location_city: data.location_city ?? null,
          customer_salutation: data.customer_salutation ?? null,
          customer_title: data.customer_title ?? null,
          customer_first_name: data.customer_first_name ?? null,
          customer_last_name: data.customer_last_name ?? null,
          customer_name: data.customer_name,
          customer_email: data.customer_email,
          customer_phone: data.customer_phone,
          customer_message: data.customer_message ?? null,
          accepted_terms: data.accepted_terms,
          sms_opt_in: data.sms_opt_in === true,
          language: currentGuestLanguage(),
          website: data.website ?? '',
        }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Anfrage fehlgeschlagen');
      }
      return (await r.json()) as { ok: true; id: string };
    },
  });
}

/**
 * Lookup für die Angebots-Seite (Kunde sieht Preis + Accept-Button).
 */
export function useApeQuoteLookup(token: string | null) {
  return useQuery({
    queryKey: ['ape', 'quote-lookup', token],
    queryFn: async (): Promise<QuoteLookup> => {
      const r = await apiFetch(
        `/api/ape-quote-lookup?token=${encodeURIComponent(token ?? '')}`,
      );
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Angebot nicht gefunden');
      }
      return (await r.json()) as QuoteLookup;
    },
    enabled: Boolean(token),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Customer akzeptiert das Angebot → Status bestaetigt.
 */
export function useApeQuoteAccept() {
  return useMutation({
    mutationFn: async (
      token: string,
    ): Promise<{ ok: true; alreadyAccepted: boolean }> => {
      const r = await apiFetch('/api/ape-quote-accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Annahme fehlgeschlagen');
      }
      return (await r.json()) as { ok: true; alreadyAccepted: boolean };
    },
  });
}

/**
 * Lookup für die Storno-Seite.
 */
export function useApeBookingLookup(token: string | null) {
  return useQuery({
    queryKey: ['ape', 'booking-lookup', token],
    queryFn: async (): Promise<BookingLookup> => {
      const r = await apiFetch(
        `/api/ape-booking-lookup?token=${encodeURIComponent(token ?? '')}`,
      );
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Buchung nicht gefunden');
      }
      return (await r.json()) as BookingLookup;
    },
    enabled: Boolean(token),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Storno-Mutation.
 */
export function useApeBookingCancel() {
  return useMutation({
    mutationFn: async (input: { token: string; reason?: string }) => {
      const r = await apiFetch('/api/ape-booking-cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: input.token,
          reason: input.reason ?? null,
        }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Stornierung fehlgeschlagen');
      }
      return (await r.json()) as { ok: true; alreadyCancelled: boolean };
    },
  });
}
